import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useUserRolePermissions } from 'hooks';
import { addDays, format, isAfter, startOfTomorrow } from 'date-fns';
import differenceInDays from 'date-fns/differenceInDays';

const buttonStyles = {
  '&, :hover': {
    boxShadow: 'none',
  },
  height: 33,
  width: 130,
};

const AlmostExpired = ({ children }) => (
  <Stack direction="row" spacing={1}>
    <Box
      sx={{
        backgroundColor: 'warning.light',
        borderRadius: 6,
        height: '30px',
        paddingTop: '5px',
        textAlign: 'center',
        width: '30px',
      }}
    >
      <PriorityHighIcon fontSize="small" sx={{ color: 'warning.dark' }} />
    </Box>
    <Typography sx={{ color: 'warning.dark' }} variant="h5">
      {children}
    </Typography>
  </Stack>
);
AlmostExpired.propTypes = { children: PropTypes.string.isRequired };

const DriverCard = ({ driver, location, subheader, title, onView, onRemove }) => {
  const { isDispatchingManage } = useUserRolePermissions();

  const expiration = new Date(driver.expirationDate);

  const isActive = driver.isActive ?? location.isActive;
  const isExpired = isAfter(startOfTomorrow(), expiration);

  const expired30Days = addDays(startOfTomorrow(), 30);
  const almostExpired = R.and(!isExpired, isAfter(expired30Days, expiration));

  const expirationDateDisplay = R.isNil(driver.expirationDate)
    ? '--'
    : almostExpired
    ? `${differenceInDays(new Date(driver.expirationDate), new Date())}d`
    : format(expiration, 'MM/dd/yy');

  const statusBoxStyles = {
    alignItems: 'center',
    backgroundColor: isActive ? 'success.light' : 'graphic.light',
    borderRadius: 0.5,
    display: 'flex',
    gridColumn: '1/3',
    height: 61,
    justifyContent: 'center',
    mb: 3,
    pb: 1,
    pt: 1,
  };

  const handleRemove = () => {
    onRemove(driver, location);
  };

  const handleView = () => {
    onView(driver, location);
  };

  const expirationLabel = R.isNil(driver.expirationDate)
    ? 'Certificate Invalid'
    : isExpired
    ? 'Certificate Expired'
    : almostExpired
    ? `Expires ${format(expiration, 'MM/dd/yy')}`
    : 'Certificate Expires';

  return (
    <Card sx={{ boxShadow: 6, width: 310 }}>
      <CardHeader
        slotProps={{
          subheader: { variant: 'body2' },
          title: { variant: 'h6' },
        }}
        subheader={subheader}
        sx={{ p: 2, pb: 0 }}
        title={R.toUpper(title)}
      />
      <CardContent
        sx={{ display: 'grid', gridColumnGap: 16, gridRowGap: 8, gridTemplateColumns: '1fr 1fr' }}
      >
        <Divider />
        <Divider />
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          {expirationLabel}
        </Typography>
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          Last Loaded
        </Typography>
        {almostExpired ? (
          <AlmostExpired>{expirationDateDisplay}</AlmostExpired>
        ) : (
          <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h5">
            {expirationDateDisplay}
          </Typography>
        )}
        <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h5">
          {driver.lastLoadedDate
            ? `${differenceInDays(new Date(), new Date(driver.lastLoadedDate))}d`
            : '--'}
        </Typography>

        <Box sx={statusBoxStyles}>
          <Typography variant="subtitle1">{isActive ? 'Active' : 'Inactive'}</Typography>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{ gridColumn: '1 / span 2', justifyContent: 'center' }}
        >
          {isDispatchingManage && (
            <Button onClick={handleRemove} sx={buttonStyles} variant="outlined">
              Remove
            </Button>
          )}
          <Button onClick={handleView} sx={buttonStyles} variant="contained">
            View
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

DriverCard.propTypes = {
  driver: PropTypes.shape({
    expirationDate: PropTypes.string,
    isActive: PropTypes.bool,
    lastLoadedDate: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    isActive: PropTypes.bool,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  subheader: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default DriverCard;
